<template>
  <div class="cms-menus">
    <cms-page-header
      :title="$t('cms.menus.title')"
      :create-text="$t('cms.menus.createNew')"
      @create="navigateToCreate"
    />

    <cms-filter-bar>
      <cms-filter-group :label="''">
        <label class="cms-public-filter">
          <input v-model="onlyPublic" type="checkbox" class="cms-public-filter-input" />
          <span class="cms-public-filter-slider">
            <span class="cms-public-filter-icon">
              <v-icon icon="eye" size="xs" />
            </span>
          </span>
          <span class="cms-public-filter-label">{{ $t('cms.menus.filters.public') }}</span>
        </label>
      </cms-filter-group>

      <cms-filter-group :label="''">
        <div class="cms-filter-actions">
          <button 
            v-if="menus.length > 0 && hasChildren"
            class="cms-btn cms-btn-secondary" 
            type="button" 
            :disabled="isLoading" 
            @click="toggleAllMenus"
          >
            <v-icon :icon="allExpanded ? 'chevron-up' : 'chevron-down'" size="sm" />
            <span>{{ allExpanded ? 'Collapse All' : 'Expand All' }}</span>
          </button>
          <button class="cms-btn cms-btn-secondary" type="button" :disabled="isLoading" @click="loadMenus">
            <v-icon icon="refresh" size="sm" />
            <span>{{ $t('general.refresh') }}</span>
          </button>
        </div>
      </cms-filter-group>
    </cms-filter-bar>

    <div v-if="errorMessage" class="cms-menus-error">
      <p>{{ errorMessage }}</p>
      <button type="button" class="cms-btn-link" @click="loadMenus">{{ $t('general.retry') }}</button>
    </div>

    <div v-if="isLoading" class="cms-menus-loading">
      <div class="cms-loading-spinner" />
      <p>{{ $t('general.loading') }}</p>
    </div>

    <cms-empty-state
      v-else-if="menus.length === 0"
      :message="$t('cms.menus.list.emptyStateTitle')"
      :hint="$t('cms.menus.list.emptyStateHint')"
    />

    <div v-else class="cms-menus-tree">
      <div
        v-for="menu in menus"
        :key="menu.id"
        class="cms-menu-card"
        :class="{ 'cms-menu-card-has-children': menu.children && menu.children.length > 0 }"
      >
        <div 
          class="cms-menu-card-header"
          :class="{ 'cms-menu-card-header-clickable': menu.children && menu.children.length > 0 }"
          @click="menu.children && menu.children.length > 0 ? toggleMenu(menu.id) : null"
        >
          <div class="cms-menu-card-content">
            <div 
              v-if="menu.children && menu.children.length > 0"
              class="cms-menu-accordion-toggle"
              @click.stop="toggleMenu(menu.id)"
            >
              <v-icon 
                :icon="expandedMenus.has(menu.id) ? 'chevron-down' : 'chevron-right'" 
                size="sm" 
              />
            </div>
            <div class="cms-menu-drag-handle" @click.stop>
              <v-icon icon="grip-vertical" size="sm" />
            </div>
            <div class="cms-menu-item-info">
              <div class="cms-menu-label-wrapper">
                <h3 class="cms-menu-label">
                  {{ menu.label }}
                  <span 
                    v-if="menu.children && menu.children.length > 0 && !expandedMenus.has(menu.id)"
                    class="cms-menu-children-count"
                  >
                    ({{ menu.children.length }})
                  </span>
                </h3>
                <div class="cms-menu-badges">
                  <span 
                    class="cms-menu-type-badge"
                    :class="`cms-menu-type-badge-${menu.menuType}`"
                  >
                    {{ menuTypeOptions[menu.menuType] }}
                  </span>
                  <span 
                    v-if="menu.isPublic"
                    class="cms-menu-status-badge cms-menu-status-published"
                  >
                    <v-icon icon="eye" size="xs" />
                    {{ $t('cms.menus.statusOptions.published') }}
                  </span>
                </div>
              </div>
              <div v-if="menu.url" class="cms-menu-url-wrapper">
                <v-icon icon="link" size="xs" />
                <span class="cms-menu-url">{{ menu.url }}</span>
              </div>
            </div>
          </div>
          <div class="cms-menu-item-actions" @click.stop>
            <cms-icon-button
              :icon="menu.isPublic ? 'eye-blocked' : 'eye'"
              :variant="menu.isPublic ? 'secondary' : 'primary'"
              :disabled="publishingId === menu.id"
              :tooltip="menu.isPublic ? $t('cms.menus.statusOptions.published') : $t('cms.menus.statusOptions.draft')"
              @click="togglePublish(menu)"
            />
            <cms-icon-button
              icon="pencil"
              variant="primary"
              :tooltip="$t('general.edit')"
              @click="navigateToEdit(menu.id)"
            />
            <cms-icon-button
              icon="trash"
              variant="danger"
              :disabled="deletingId === menu.id"
              :tooltip="$t('general.delete')"
              @click="deleteMenu(menu)"
            />
          </div>
        </div>

        <!-- Children (submenus) -->
        <transition name="cms-menu-accordion">
          <div v-if="menu.children && menu.children.length > 0 && expandedMenus.has(menu.id)" class="cms-menu-children">
          <div
            v-for="child in menu.children"
            :key="child.id"
            class="cms-menu-child-card"
          >
            <div class="cms-menu-card-header">
              <div class="cms-menu-card-content">
                <div class="cms-menu-drag-handle">
                  <v-icon icon="grip-vertical" size="sm" />
                </div>
                <div class="cms-menu-item-info">
                  <div class="cms-menu-label-wrapper">
                    <h4 class="cms-menu-label cms-menu-label-child">{{ child.label }}</h4>
                    <div class="cms-menu-badges">
                      <span 
                        class="cms-menu-type-badge"
                        :class="`cms-menu-type-badge-${child.menuType}`"
                      >
                        {{ menuTypeOptions[child.menuType] }}
                      </span>
                      <span 
                        v-if="child.isPublic"
                        class="cms-menu-status-badge cms-menu-status-published"
                      >
                        <v-icon icon="eye" size="xs" />
                        {{ $t('cms.menus.statusOptions.published') }}
                      </span>
                    </div>
                  </div>
                  <div v-if="child.url" class="cms-menu-url-wrapper">
                    <v-icon icon="link" size="xs" />
                    <span class="cms-menu-url">{{ child.url }}</span>
                  </div>
                </div>
              </div>
              <div class="cms-menu-item-actions">
                <cms-icon-button
                  :icon="child.isPublic ? 'eye-blocked' : 'eye'"
                  :variant="child.isPublic ? 'secondary' : 'primary'"
                  :disabled="publishingId === child.id"
                  :tooltip="child.isPublic ? $t('cms.menus.statusOptions.published') : $t('cms.menus.statusOptions.draft')"
                  @click="togglePublish(child)"
                />
                <cms-icon-button
                  icon="pencil"
                  variant="primary"
                  :tooltip="$t('general.edit')"
                  @click="navigateToEdit(child.id)"
                />
                <cms-icon-button
                  icon="trash"
                  variant="danger"
                  :disabled="deletingId === child.id"
                  :tooltip="$t('general.delete')"
                  @click="deleteMenu(child)"
                />
              </div>
            </div>
          </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import {
  CmsPageHeader,
  CmsFilterBar,
  CmsFilterGroup,
  CmsIconButton,
  CmsEmptyState,
} from '@/components/cms';
import api from '@/api';
import { useAuthStore } from '@/stores/auth';
import { useAppLanguages } from '@/composables/useAppLanguages';
import { normalizeResource, extractRelationship } from '@/composables/useCmsActivityData';

interface CmsMenuCard {
  id: number;
  label: string;
  menuType: 'page' | 'link' | 'category' | 'activity';
  url: string;
  isPublic: boolean;
  children?: CmsMenuCard[];
}

export default defineComponent({
  name: 'TheCmsMenus',
  components: {
    CmsPageHeader,
    CmsFilterBar,
    CmsFilterGroup,
    CmsIconButton,
    CmsEmptyState,
    VIcon,
  },
  setup() {
    const router = useRouter();
    const toast = useToast();
    const { t } = useI18n();
    const authStore = useAuthStore();

    const agencySlug = computed(() => authStore.agency?.slug ?? null);
    const { resolveStoredLocale } = useAppLanguages();
    const currentLocale = ref(resolveStoredLocale(localStorage.getItem('language')));

    const menus = ref<CmsMenuCard[]>([]);
    const isLoading = ref(false);
    const errorMessage = ref('');
    const publishingId = ref<number | null>(null);
    const deletingId = ref<number | null>(null);
    const expandedMenus = ref<Set<number>>(new Set());

    const onlyPublic = ref(false);

    const menuTypeOptions = computed(() => ({
      page: t('cms.menus.menuTypeOptions.page'),
      link: t('cms.menus.menuTypeOptions.link'),
      category: t('cms.menus.menuTypeOptions.category'),
      activity: t('cms.menus.menuTypeOptions.activity'),
    }));

    const hasChildren = computed(() => {
      return menus.value.some(menu => menu.children && menu.children.length > 0);
    });

    const allExpanded = computed(() => {
      if (!hasChildren.value) return false;
      return menus.value
        .filter(menu => menu.children && menu.children.length > 0)
        .every(menu => expandedMenus.value.has(menu.id));
    });

    const toggleMenu = (menuId: number) => {
      if (expandedMenus.value.has(menuId)) {
        expandedMenus.value.delete(menuId);
      } else {
        expandedMenus.value.add(menuId);
      }
      // Trigger reactivity
      expandedMenus.value = new Set(expandedMenus.value);
    };

    const toggleAllMenus = () => {
      const menusWithChildren = menus.value.filter(menu => menu.children && menu.children.length > 0);
      if (allExpanded.value) {
        // Collapse all
        expandedMenus.value.clear();
      } else {
        // Expand all
        menusWithChildren.forEach(menu => expandedMenus.value.add(menu.id));
      }
      expandedMenus.value = new Set(expandedMenus.value);
    };

    const extractListData = (response: any): any[] => {
      if (!response) return [];
      const candidates = [
        response?.data?.data,
        response?.data,
        response?.data?.data?.data,
        response,
      ];
      for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
          return candidate;
        }
      }
      return [];
    };

    const getMenuUrl = (menu: any): string => {
      if (menu.menu_type === 'link') {
        return menu.external_url || '';
      }
      if (menu.menu_type === 'category') {
        return `/categories/${menu.category_slug}`;
      }
      if (menu.menu_type === 'page' && menu.page?.custom_url) {
        return `/${menu.page.custom_url}`;
      }
      return '';
    };

    const mapMenuRecord = (item: any, locale: string): CmsMenuCard => {
      const normalized = normalizeResource(item) || item;
      const translations = extractRelationship(normalized, 'translations');
      const normalizedTranslations = normalizeResource(translations);
      const translation =
        (Array.isArray(normalizedTranslations)
          ? normalizedTranslations.find((tr: any) => (tr?.locale || '').toLowerCase() === locale)
          : normalizedTranslations) ||
        (Array.isArray(normalized.translations)
          ? normalized.translations.find((tr: any) => (tr?.locale || '').toLowerCase() === locale)
          : normalized.translations?.[0]) ||
        {};

      const children = normalized.children
        ? normalized.children.map((child: any) => mapMenuRecord(child, locale))
        : [];
        
      return {
        id: normalized.id,
        label: translation?.label || normalized.label || '-',
        menuType: normalized.menuType || 'page',
        url: getMenuUrl(normalized),
        isPublic: !!normalized.isPublic,
        children: children.length > 0 ? children : undefined,
      };
    };

    const buildFilters = () => {
      const filters: Record<string, any> = {
        language: currentLocale.value,
      };

      if (onlyPublic.value) {
        filters.is_public = 1;
      }

      return filters;
    };

    const loadMenus = async () => {
      if (!agencySlug.value) {
        return;
      }

      isLoading.value = true;
      errorMessage.value = '';

      try {
        const response = await api.menuService.listCmsMenus(agencySlug.value, {
          filters: buildFilters(),
        });

        const list = extractListData(response);
        menus.value = list.map((item: any) => mapMenuRecord(item, currentLocale.value));
        // Keep menus collapsed by default
        expandedMenus.value = new Set();
      } catch (error: any) {
        console.error('Failed to load CMS menus', error);
        errorMessage.value = error?.response?.data?.message || t('general.errorMessage');
        toast.error(errorMessage.value);
      } finally {
        isLoading.value = false;
      }
    };

    watch(
      () => agencySlug.value,
      (slug) => {
        if (slug) {
          loadMenus();
        }
      },
      { immediate: true },
    );

    watch(
      () => onlyPublic.value,
      () => {
        if (!agencySlug.value) return;
        loadMenus();
      },
    );

    const navigateToCreate = () => {
      router.push({ name: 'cms.menus.create' });
    };

    const navigateToEdit = (menuId: number) => {
      router.push({ name: 'cms.menus.edit', params: { id: menuId } });
    };

    const togglePublish = async (menu: CmsMenuCard) => {
      if (!agencySlug.value) return;

      const nextState = !menu.isPublic;
      publishingId.value = menu.id;

      try {
        await api.menuService.updateCmsMenu(agencySlug.value, menu.id, {
          is_public: nextState,
        });
        menus.value = updateMenuInTree(menus.value, menu.id, { isPublic: nextState });
        toast.success(nextState ? t('cms.menus.publishSuccess') : t('cms.menus.unpublishSuccess'));
      } catch (error: any) {
        console.error('Failed to toggle publish state', error);
        const message = error?.response?.data?.message || t('general.errorMessage');
        toast.error(message);
      } finally {
        publishingId.value = null;
      }
    };

    const updateMenuInTree = (menuList: CmsMenuCard[], id: number, updates: Partial<CmsMenuCard>): CmsMenuCard[] => {
      return menuList.map((menu) => {
        if (menu.id === id) {
          return { ...menu, ...updates };
        }
        if (menu.children) {
          return { ...menu, children: updateMenuInTree(menu.children, id, updates) };
        }
        return menu;
      });
    };

    const deleteMenu = async (menu: CmsMenuCard) => {
      if (!agencySlug.value) return;
      if (!window.confirm(t('general.deleteConfirmation') || 'Are you sure?')) {
        return;
      }

      deletingId.value = menu.id;
      try {
        await api.menuService.deleteCmsMenu(agencySlug.value, menu.id);
        menus.value = removeMenuFromTree(menus.value, menu.id);
        toast.success(t('cms.menus.deleteSuccess'));
      } catch (error: any) {
        console.error('Failed to delete menu', error);
        const message = error?.response?.data?.message || t('general.errorMessage');
        toast.error(message);
      } finally {
        deletingId.value = null;
      }
    };

    const removeMenuFromTree = (menuList: CmsMenuCard[], id: number): CmsMenuCard[] => {
      return menuList
        .filter((menu) => menu.id !== id)
        .map((menu) => {
          if (menu.children) {
            return { ...menu, children: removeMenuFromTree(menu.children, id) };
          }
          return menu;
        });
    };

    return {
      menus,
      onlyPublic,
      menuTypeOptions,
      isLoading,
      errorMessage,
      publishingId,
      deletingId,
      expandedMenus,
      hasChildren,
      allExpanded,
      toggleMenu,
      toggleAllMenus,
      loadMenus,
      navigateToCreate,
      navigateToEdit,
      togglePublish,
      deleteMenu,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/components/cms/cms-styles.scss';

.cms-menus {
  color: $cms-text-primary;
}

.cms-toggle-group {
  display: flex;
  gap: $cms-spacing-sm;
  flex-wrap: wrap;
}

.cms-filter-actions {
  display: flex;
  gap: $cms-spacing-sm;
  align-items: center;
  flex-wrap: wrap;
}

:deep(.cms-filter-group:has(.cms-filter-actions)) {
  min-width: auto;
  flex: 0 0 auto;
}

.cms-public-filter {
  display: inline-flex;
  align-items: center;
  gap: $cms-spacing-md;
  cursor: pointer;
  user-select: none;
  position: relative;
  padding: $cms-spacing-xs $cms-spacing-sm;
  border-radius: $cms-radius-md;
  transition: all $cms-transition-fast;
  
  &:hover {
    background: $cms-background-light;
  }
  
  &-input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    
    &:checked + .cms-public-filter-slider {
      background: linear-gradient(135deg, $cms-primary-color 0%, darken($cms-primary-color, 5%) 100%);
      border-color: $cms-primary-color;
    }
    
    &:focus + .cms-public-filter-slider {
      box-shadow: 0 0 0 3px rgba($cms-primary-color, 0.2);
    }
  }
  
  &-slider {
    position: relative;
    display: inline-flex;
    align-items: center;
    width: 48px;
    height: 26px;
    background: $cms-background-gray;
    border: 2px solid $cms-border-color;
    border-radius: 13px;
    transition: all $cms-transition-base;
    flex-shrink: 0;
    overflow: hidden;
    
    
    &:hover {
      border-color: $cms-primary-color;
      box-shadow: 0 0 0 3px rgba($cms-primary-color, 0.1);
    }
  }
  
  &-icon {
    position: absolute;
    left: 3px;
    top: 50%;
    transform: translateY(-50%) scale(0.85);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    background: white;
    border-radius: 50%;
    transition: all $cms-transition-base;
    color: $cms-text-secondary;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
    z-index: 3;
    
    :deep(.icon) {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 11px;
      line-height: 1;
      width: 100%;
      height: 100%;
    }
    
    :deep(svg),
    :deep(path) {
      fill: currentColor;
    }
  }
  
  &-input:checked + &-slider &-icon {
    left: auto;
    right: 3px;
    transform: translateY(-50%) scale(1);
    color: white;
    background: rgba(255, 255, 255, 0.25);
    
    :deep(.icon) {
      color: white;
    }
    
    :deep(svg),
    :deep(path) {
      fill: white;
      stroke: white;
    }
  }
  
  &-label {
    font-size: $cms-font-size-sm;
    color: $cms-text-primary;
    font-weight: 500;
    transition: all $cms-transition-fast;
    white-space: nowrap;
  }
  
  &:hover &-label {
    color: $cms-primary-color;
  }
  
  &-input:checked ~ &-label {
    color: $cms-primary-color;
    font-weight: 600;
  }
}

.cms-menus-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: $cms-spacing-xxl 0;
  gap: $cms-spacing-sm;
  color: $cms-text-secondary;
}

.cms-loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top-color: $cms-primary-color;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.cms-menus-error {
  display: flex;
  align-items: center;
  gap: $cms-spacing-md;
  padding: $cms-spacing-md;
  margin-bottom: $cms-spacing-md;
  background: $cms-danger-light;
  border: 1px solid $cms-danger-border;
  border-radius: $cms-radius-md;
  color: $cms-danger-color;
}

.cms-btn-link {
  background: none;
  border: none;
  padding: 0;
  color: $cms-primary-color;
  cursor: pointer;
  font-weight: 500;
  text-decoration: underline;
}

.cms-menus-tree {
  display: flex;
  flex-direction: column;
  gap: $cms-spacing-lg;
}

.cms-menu-card {
  background: $cms-background-white;
  border-radius: $cms-radius-lg;
  padding: $cms-spacing-lg;
  border: 1px solid $cms-border-color;
  box-shadow: $cms-shadow-sm;
  transition: all $cms-transition-base;
  
  &:hover {
    box-shadow: $cms-shadow-md;
    transform: translateY(-2px);
    border-color: rgba($cms-primary-color, 0.2);
  }
  
  &.cms-menu-card-has-children {
    .cms-menu-card-header-clickable {
      cursor: pointer;
      
      &:hover {
        .cms-menu-label {
          color: $cms-primary-color;
        }
      }
    }
  }
}

.cms-menu-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: $cms-spacing-md;
  
  &.cms-menu-card-header-clickable {
    user-select: none;
  }
}

.cms-menu-card-content {
  display: flex;
  align-items: flex-start;
  gap: $cms-spacing-sm;
  flex: 1;
  min-width: 0;
}

.cms-menu-accordion-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: $cms-spacing-xs;
  border-radius: $cms-radius-sm;
  transition: all $cms-transition-fast;
  color: $cms-text-secondary;
  flex-shrink: 0;
  margin-top: 2px;
  
  &:hover {
    background: $cms-background-light;
    color: $cms-primary-color;
  }
  
  :deep(.icon) {
    transition: transform $cms-transition-base;
  }
}

.cms-menu-drag-handle {
  color: $cms-text-muted;
  cursor: grab;
  padding: $cms-spacing-xs;
  border-radius: $cms-radius-sm;
  transition: all $cms-transition-fast;
  flex-shrink: 0;
  margin-top: 2px;
  
  &:hover {
    color: $cms-text-primary;
    background: $cms-background-light;
  }
  
  &:active {
    cursor: grabbing;
  }
}

.cms-menu-item-info {
  display: flex;
  flex-direction: column;
  gap: $cms-spacing-xs;
  flex: 1;
  min-width: 0;
}

.cms-menu-label-wrapper {
  display: flex;
  align-items: center;
  gap: $cms-spacing-sm;
  flex-wrap: wrap;
}

.cms-menu-label {
  font-size: $cms-font-size-lg;
  font-weight: 600;
  margin: 0;
  color: $cms-text-primary;
  line-height: 1.4;
  transition: color $cms-transition-fast;
  
  &.cms-menu-label-child {
    font-size: $cms-font-size-md;
    font-weight: 500;
  }
}

.cms-menu-children-count {
  font-size: $cms-font-size-sm;
  font-weight: 400;
  color: $cms-text-secondary;
  margin-left: $cms-spacing-xs;
}

.cms-menu-badges {
  display: flex;
  align-items: center;
  gap: $cms-spacing-xs;
  flex-wrap: wrap;
}

.cms-menu-type-badge {
  display: inline-flex;
  align-items: center;
  padding: $cms-spacing-xxs $cms-spacing-sm;
  border-radius: $cms-radius-sm;
  font-size: $cms-font-size-xs;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all $cms-transition-fast;
  
  &.cms-menu-type-badge-page {
    background: rgba($cms-primary-color, 0.1);
    color: $cms-primary-color;
    border: 1px solid rgba($cms-primary-color, 0.2);
  }
  
  &.cms-menu-type-badge-link {
    background: rgba($cms-info-color, 0.1);
    color: $cms-info-color;
    border: 1px solid rgba($cms-info-color, 0.2);
  }
  
  &.cms-menu-type-badge-category {
    background: rgba($cms-secondary-color, 0.15);
    color: darken($cms-secondary-color, 30%);
    border: 1px solid rgba($cms-secondary-color, 0.3);
  }

  &.cms-menu-type-badge-activity {
    background: rgba($cms-success-color, 0.1);
    color: $cms-success-color;
    border: 1px solid rgba($cms-success-color, 0.2);
  }
}

.cms-menu-status-badge {
  display: inline-flex;
  align-items: center;
  gap: $cms-spacing-xxs;
  padding: $cms-spacing-xxs $cms-spacing-sm;
  border-radius: $cms-radius-sm;
  font-size: $cms-font-size-xs;
  font-weight: 500;
  
  &.cms-menu-status-published {
    background: $cms-success-light;
    color: $cms-success-dark;
    border: 1px solid rgba($cms-success-color, 0.3);
  }
}

.cms-menu-url-wrapper {
  display: flex;
  align-items: center;
  gap: $cms-spacing-xs;
  margin-top: $cms-spacing-xs;
  padding: $cms-spacing-xs $cms-spacing-sm;
  background: $cms-background-light;
  border-radius: $cms-radius-sm;
  width: fit-content;
  
  .cms-menu-url {
    font-size: $cms-font-size-sm;
    color: $cms-text-secondary;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    word-break: break-all;
  }
}

.cms-menu-item-actions {
  display: flex;
  gap: $cms-spacing-xs;
  flex-shrink: 0;
}

.cms-menu-children {
  margin-top: $cms-spacing-lg;
  padding-left: $cms-spacing-xl;
  padding-top: $cms-spacing-lg;
  border-left: 3px solid rgba($cms-primary-color, 0.15);
  display: flex;
  flex-direction: column;
  gap: $cms-spacing-md;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: -3px;
    top: 0;
    width: 3px;
    height: $cms-spacing-lg;
    background: linear-gradient(to bottom, rgba($cms-primary-color, 0.15), transparent);
  }
}

.cms-menu-child-card {
  background: $cms-background-gray-light;
  border-radius: $cms-radius-md;
  padding: $cms-spacing-md;
  border: 1px solid $cms-border-color-gray;
  transition: all $cms-transition-base;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: -$cms-spacing-xl;
    top: 50%;
    transform: translateY(-50%);
    width: $cms-spacing-md;
    height: 2px;
    background: rgba($cms-primary-color, 0.2);
  }
  
  &:hover {
    background: $cms-background-white;
    box-shadow: $cms-shadow-sm;
    transform: translateX(4px);
    border-color: rgba($cms-primary-color, 0.15);
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

// Accordion transition
.cms-menu-accordion-enter-active,
.cms-menu-accordion-leave-active {
  transition: all $cms-transition-base;
  overflow: hidden;
}

.cms-menu-accordion-enter-from {
  opacity: 0;
  max-height: 0;
  margin-top: 0;
  padding-top: 0;
}

.cms-menu-accordion-enter-to {
  opacity: 1;
  max-height: 5000px;
}

.cms-menu-accordion-leave-from {
  opacity: 1;
  max-height: 5000px;
}

.cms-menu-accordion-leave-to {
  opacity: 0;
  max-height: 0;
  margin-top: 0;
  padding-top: 0;
}
</style>

